import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  entrepriseService,
  succursaleService,
} from '../services/entrepriseService';
import {
  InfoEntrepriseCreateDto,
  InfoEntrepriseUpdateDto,
  SuccursaleCreateDto,
  SuccursaleUpdateDto,
  PaginatedResponse,
  InfoEntrepriseDto,
  SuccursaleDto,
} from '../types/django';

export const useEntreprises = () => {
  return useQuery({
    queryKey: ['entreprises'],
    queryFn: entrepriseService.getAll,
  });
};

export const useInfiniteEntreprises = (
  search: string = '',
  pageSize: number = 25
) => {
  return useInfiniteQuery<PaginatedResponse<InfoEntrepriseDto>>({
    queryKey: ['entreprises-paginated', search, pageSize],
    queryFn: async ({ pageParam }) => {
      const response = await entrepriseService.getPaginated({
        page: pageParam as number,
        page_size: pageSize,
        search: search || undefined,
      });

      if (
        response &&
        typeof response === 'object' &&
        'results' in response &&
        'count' in response
      ) {
        return response as PaginatedResponse<InfoEntrepriseDto>;
      }

      const results = Array.isArray(response) ? response : [];

      return {
        count: results.length,
        next: null,
        previous: null,
        results,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? (lastPageParam as number) + 1 : undefined,
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
      (firstPageParam as number) > 1
        ? (firstPageParam as number) - 1
        : undefined,
  });
};

export const useEntreprise = (id?: number) => {
  return useQuery({
    queryKey: ['entreprise', id ?? 'current'],
    queryFn: () => entrepriseService.getById(id),
  });
};
export const useCreateEntreprise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InfoEntrepriseCreateDto) =>
      entrepriseService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
    },
  });
};

export const useUpdateEntreprise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: InfoEntrepriseUpdateDto;
    }) => entrepriseService.update(id, data),

    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
   
      queryClient.invalidateQueries({
        queryKey: ['entreprise'],
      });
    },
  });
};

export const useDeleteEntreprise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: entrepriseService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
    },
  });
};

/* =========================
   SUCCURSALES
========================= */

export const useSuccursales = (entrepriseId?: number) => {
  return useQuery({
    queryKey: ['succursales', entrepriseId],
    queryFn: () => succursaleService.getAll(entrepriseId),
  });
};

export const useInfiniteSuccursales = (
  search: string = '',
  pageSize: number = 25,
  entrepriseId?: number
) => {
  return useInfiniteQuery<PaginatedResponse<SuccursaleDto>>({
    queryKey: [
      'succursales-paginated',
      search,
      pageSize,
      entrepriseId,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await succursaleService.getPaginated({
        page: pageParam as number,
        page_size: pageSize,
        search: search || undefined,
        entreprise: entrepriseId,
      });

      if (
        response &&
        typeof response === 'object' &&
        'results' in response &&
        'count' in response
      ) {
        return response as PaginatedResponse<SuccursaleDto>;
      }

      const results = Array.isArray(response) ? response : [];

      return {
        count: results.length,
        next: null,
        previous: null,
        results,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? (lastPageParam as number) + 1 : undefined,
  });
};

export const useCreateSuccursale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SuccursaleCreateDto) =>
      succursaleService.create(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['succursales'] });
      queryClient.invalidateQueries({
        queryKey: ['succursales-paginated'],
      });
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
    },
  });
};

export const useUpdateSuccursale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: SuccursaleUpdateDto;
    }) => succursaleService.update(id, data),

    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['succursales'] });
      queryClient.invalidateQueries({
        queryKey: ['succursales-paginated'],
      });
      queryClient.invalidateQueries({
        queryKey: ['succursale', id],
      });
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
    },
  });
};

export const useDeleteSuccursale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: succursaleService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['succursales'] });
      queryClient.invalidateQueries({
        queryKey: ['succursales-paginated'],
      });
      queryClient.invalidateQueries({ queryKey: ['entreprises'] });
      queryClient.invalidateQueries({
        queryKey: ['entreprises-paginated'],
      });
    },
  });
};